import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, Loader2, Save } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

/**
 * Dialog to edit game rules.
 * - Textarea for manual content (markdown)
 * - URL field for external rules link
 * - "Generate with AI" button (server uses merchant info + game segments)
 */
export default function RulesDialog({ gameId, open, onOpenChange }) {
  const { t } = useTranslation();
  const [content, setContent] = useState("");
  const [url, setUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && gameId) {
      setLoading(true);
      api
        .get(`/games/${gameId}`)
        .then((r) => {
          setContent(r.data.rules_content || "");
          setUrl(r.data.rules_url || "");
        })
        .finally(() => setLoading(false));
    }
  }, [open, gameId]);

  async function generate() {
    setGenerating(true);
    try {
      const { data } = await api.post(`/games/${gameId}/rules/generate`);
      setContent(data.rules_content || "");
      toast.success(t("rules.generated"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/games/${gameId}/rules`, {
        rules_content: content || null,
        rules_url: url || null,
      });
      toast.success(t("rules.saved"));
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[88vh] flex flex-col"
        data-testid="rules-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t("rules.title")}</DialogTitle>
          <p className="text-sm text-muted-foreground pt-1">
            {t("rules.subtitle")}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4 pr-1">
          <div>
            <Label className="text-xs font-medium">
              {t("rules.external_url")}
            </Label>
            <Input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://"
              className="mt-1.5 rounded-xl h-10"
              data-testid="rules-url-input"
            />
            <div className="mt-1 text-[10px] text-muted-foreground">
              {t("rules.external_url_hint")}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-medium">
                {t("rules.content")}
              </Label>
              <Button
                size="sm"
                variant="outline"
                onClick={generate}
                disabled={generating || loading}
                className="h-8 rounded-lg text-xs"
                data-testid="rules-generate-btn"
              >
                {generating ? (
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3 mr-1.5" />
                )}
                {generating ? t("rules.generating") : t("rules.generate")}
              </Button>
            </div>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={18}
              placeholder={t("rules.placeholder")}
              className="rounded-xl font-mono text-sm leading-relaxed"
              data-testid="rules-content-textarea"
            />
            <div className="mt-1 text-[10px] text-muted-foreground">
              {t("rules.markdown_hint")}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="rules-cancel-btn"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-foreground text-background hover:bg-foreground/90"
            data-testid="rules-save-btn"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? "…" : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
